import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerEstilosAdmin, guardarEstilos, ESTILOS_DEFAULT } from '../../services/themeService';
import { obtenerDisponibilidad, guardarDisponibilidad, obtenerConfiguracion } from '../../services/configTiendaApi';
import { obtenerSectores, eliminarSector } from '../../services/ConfiguracionServicio';
import { usePlan } from '../context/PlanContext';
import { TEMAS_PREDEFINIDOS } from './tiendaConstants';
import { TemaCard } from './tiendaSubComponents';
import { SectorFormModal } from './AdministradorDespachos';
import './ConfiguradorTienda.css';

const formatPrecioCLP = (valor) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(valor || 0);

const DIAS_SEMANA = [
    { key: 'lunes', nombre: 'Lunes' },
    { key: 'martes', nombre: 'Martes' },
    { key: 'miercoles', nombre: 'Miércoles' },
    { key: 'jueves', nombre: 'Jueves' },
    { key: 'viernes', nombre: 'Viernes' },
    { key: 'sabado', nombre: 'Sábado' },
    { key: 'domingo', nombre: 'Domingo' },
];

const DIA_VACIO = () => ({ activo: false, desde: '09:00', hasta: '22:00' });

const METODOS_PAGO_DISPONIBLES = [
    { id: 'efectivo', label: '💵 Efectivo' },
    { id: 'tarjeta', label: '💳 Tarjeta (débito/crédito)' },
    { id: 'transferencia', label: '🏦 Transferencia' },
];

const METODOS_PAGO_DEFAULT = {
    delivery: ['efectivo', 'transferencia'],
    retiro: ['efectivo', 'transferencia', 'tarjeta'],
};

const DATOS_TRANSFERENCIA_VACIO = { banco: '', tipo_cuenta: '', numero_cuenta: '', titular: '', rut: '', email: '' };

const FUENTES_DISPONIBLES = [
    { valor: "'Inter', sans-serif", nombre: 'Inter (moderna)' },
    { valor: "'Poppins', sans-serif", nombre: 'Poppins (redondeada)' },
    { valor: "'Roboto', sans-serif", nombre: 'Roboto (clásica)' },
    { valor: "'Montserrat', sans-serif", nombre: 'Montserrat (elegante)' },
    { valor: "Georgia, serif", nombre: 'Georgia (con serifa)' },
];

const RADIOS_DISPONIBLES = [
    { valor: '4px', nombre: 'Recto' },
    { valor: '12px', nombre: 'Redondeado' },
    { valor: '24px', nombre: 'Muy redondeado' },
];

// Menú lateral de secciones -- cada item salta a la tarjeta correspondiente
// (id igual al de su ShopifyCard).
const SECCIONES = [
    { id: 'seccion-estado', label: 'Estado', icono: 'bi bi-toggle-on' },
    { id: 'seccion-horario', label: 'Horario', icono: 'bi bi-clock' },
    { id: 'seccion-pagos', label: 'Métodos de pago', icono: 'bi bi-credit-card' },
    { id: 'seccion-despacho', label: 'Zonas de despacho', icono: 'bi bi-geo-alt-fill' },
    { id: 'seccion-diseno', label: 'Diseño', icono: 'bi bi-palette2' },
    { id: 'seccion-vista-catalogo', label: 'Vista catálogo', icono: 'bi bi-grid-3x3-gap' },
    { id: 'seccion-qr', label: 'Código QR', icono: 'bi bi-qr-code' },
];

function ShopifyCard({ id, icono, titulo, children, activa = true }) {
    if (!activa) return null;
    return (
        <div className="shopify-card" id={id}>
            <div className="shopify-card-header">
                <div className="shopify-card-icon"><i className={icono}></i></div>
                <h6 className="shopify-card-title">{titulo}</h6>
            </div>
            {children}
        </div>
    );
}

function ConfiguradorPedidosWeb() {
    const { tienePedidosWeb } = usePlan();
    // Qué sección se muestra en el panel central -- el menú de la izquierda
    // cambia esto, y solo esa tarjeta se renderiza (no todas apiladas).
    const [seccionActiva, setSeccionActiva] = useState(SECCIONES[0].id);
    const [estilos, setEstilos] = useState(ESTILOS_DEFAULT);
    const [loading, setLoading] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [cambiosPendientes, setCambiosPendientes] = useState(false);

    // 🚩 Disponibilidad: horario en que se aceptan pedidos + interruptor
    // manual de emergencia. Guardado aparte de los estilos (endpoint propio).
    const [abierto, setAbierto] = useState(true);
    const [horario, setHorario] = useState(() => {
        const base = {};
        DIAS_SEMANA.forEach(d => { base[d.key] = DIA_VACIO(); });
        return base;
    });
    const [guardandoDisp, setGuardandoDisp] = useState(false);
    const [cambiosDispPendientes, setCambiosDispPendientes] = useState(false);

    // 🚩 Métodos de pago habilitados por tipo de entrega + datos bancarios
    // (se guardan junto con horario/abierto, mismo endpoint de disponibilidad).
    const [metodosPago, setMetodosPago] = useState(METODOS_PAGO_DEFAULT);
    const [datosTransferencia, setDatosTransferencia] = useState(DATOS_TRANSFERENCIA_VACIO);

    const toggleMetodoPago = (tipo, metodoId) => {
        setMetodosPago(prev => {
            const actuales = prev[tipo] || [];
            const nuevos = actuales.includes(metodoId)
                ? actuales.filter(m => m !== metodoId)
                : [...actuales, metodoId];
            return { ...prev, [tipo]: nuevos };
        });
        setCambiosDispPendientes(true);
    };

    const setCampoTransferencia = (campo, valor) => {
        setDatosTransferencia(prev => ({ ...prev, [campo]: valor }));
        setCambiosDispPendientes(true);
    };

    // 🚩 Modo de cobro de despacho: gratis / monto fijo / por sectores (el
    // sistema de zonas de más abajo). Gratis y fijo no requieren zonas.
    const [modoDespacho, setModoDespacho] = useState('zonas');
    const [costoDespachoFijo, setCostoDespachoFijo] = useState(0);
    // 🚩 Cuando modoDespacho === 'zonas': UN solo enfoque para TODAS las zonas
    // del negocio (no se mezclan geocerca y nombre en la misma lista, para que
    // el checkout muestre una sola cosa — mapa o lista — nunca ambas).
    const [tipoZonas, setTipoZonas] = useState('geocerca');

    const cambiarModoDespacho = (modo) => {
        setModoDespacho(modo);
        setCambiosDispPendientes(true);
    };

    const cambiarTipoZonas = (tipo) => {
        setTipoZonas(tipo);
        setCambiosDispPendientes(true);
    };

    const transferenciaHabilitada = (metodosPago.delivery || []).includes('transferencia') || (metodosPago.retiro || []).includes('transferencia');

    // 🚩 Solo lectura acá: el slug se edita en Carta QR (misma columna,
    // compartida por /slug y /slug/web). Acá solo se usa para armar el QR.
    const [urlSlug, setUrlSlug] = useState('');

    // 🚩 Zonas de despacho, integradas acá para no tener que salir de la
    // configuración de la tienda a "Ajustes" a cada rato. Reutiliza el mismo
    // modal y servicio que AdministradorDespachos.js.
    const [sectores, setSectores] = useState([]);
    const [cargandoSectores, setCargandoSectores] = useState(true);
    const [modalSectorAbierto, setModalSectorAbierto] = useState(false);
    const [sectorAEditar, setSectorAEditar] = useState(null);

    const cargarSectores = async () => {
        setCargandoSectores(true);
        try {
            const data = await obtenerSectores();
            const procesados = data.map(s => {
                let poli = s.poligono;
                if (typeof poli === 'string' && poli !== "") {
                    try { poli = JSON.parse(poli); } catch (e) { poli = null; }
                }
                return { ...s, poligono: poli };
            });
            setSectores(procesados || []);
        } catch (err) {
            console.error('Error cargando zonas de despacho:', err);
        } finally {
            setCargandoSectores(false);
        }
    };

    const handleEliminarSector = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar la zona "${nombre}"?`)) return;
        try {
            await eliminarSector(id);
            toast.success('Zona eliminada');
            cargarSectores();
        } catch (err) {
            toast.error('No se pudo eliminar');
        }
    };

    const handleGuardarSector = () => {
        setModalSectorAbierto(false);
        setSectorAEditar(null);
        toast.success('¡Zona guardada!');
        cargarSectores();
    };

    useEffect(() => {
        const cargar = async () => {
            setCargando(true);
            const data = await obtenerEstilosAdmin();
            setEstilos(data);
            setCargando(false);
        };
        cargar();

        obtenerConfiguracion().then(data => {
            if (data && data.urlSlug) setUrlSlug(data.urlSlug);
        }).catch(() => {});

        cargarSectores();

        const cargarDisponibilidad = async () => {
            try {
                const disp = await obtenerDisponibilidad();
                if (typeof disp.abierto === 'boolean') setAbierto(disp.abierto);
                if (disp.horarioPedidos) {
                    setHorario(prev => {
                        const combinado = { ...prev };
                        DIAS_SEMANA.forEach(d => {
                            if (disp.horarioPedidos[d.key]) combinado[d.key] = { ...DIA_VACIO(), ...disp.horarioPedidos[d.key] };
                        });
                        return combinado;
                    });
                }
                if (disp.metodosPago) {
                    setMetodosPago(prev => ({ ...prev, ...disp.metodosPago }));
                }
                if (disp.datosTransferencia) {
                    setDatosTransferencia(prev => ({ ...prev, ...disp.datosTransferencia }));
                }
                if (disp.modoDespacho) setModoDespacho(disp.modoDespacho);
                if (typeof disp.costoDespachoFijo === 'number') setCostoDespachoFijo(disp.costoDespachoFijo);
                if (disp.tipoZonas) setTipoZonas(disp.tipoZonas);
            } catch (err) {
                console.error('Error cargando disponibilidad:', err);
            }
        };
        cargarDisponibilidad();
    }, []);

    const setDiaHorario = (dia, campo, valor) => {
        setHorario(prev => ({ ...prev, [dia]: { ...prev[dia], [campo]: valor } }));
        setCambiosDispPendientes(true);
    };

    const toggleAbierto = (valor) => {
        setAbierto(valor);
        setCambiosDispPendientes(true);
    };

    const handleGuardarDisponibilidad = async () => {
        setGuardandoDisp(true);
        try {
            // 🚩 Si ningún día quedó marcado "activo", NO es un horario configurado
            // (es el estado inicial vacío) — mandar null en vez del objeto completo,
            // porque un horario con todos los días inactivos significa "cerrado
            // siempre" para el cálculo de estaAbierta en Pedidos.js.
            const hayHorarioReal = Object.values(horario).some(d => d.activo);
            const res = await guardarDisponibilidad({ abierto, horarioPedidos: hayHorarioReal ? horario : null, metodosPago, datosTransferencia, modoDespacho, costoDespachoFijo, tipoZonas });
            if (res && !res.error) {
                toast.success('Disponibilidad guardada');
                setCambiosDispPendientes(false);
                // Si se cambió el enfoque de zonas a "nombre", el backend
                // limpió los polígonos viejos -- recargamos para que la
                // lista/badges reflejen eso al instante.
                cargarSectores();
            } else {
                toast.error(res.error || 'Error al guardar');
            }
        } catch (err) {
            toast.error('Error de conexión al guardar');
        } finally {
            setGuardandoDisp(false);
        }
    };

    const pw = { ...ESTILOS_DEFAULT.pedidos_web, ...(estilos.pedidos_web || {}) };
    const urlTienda = urlSlug ? `${window.location.origin}/${urlSlug}` : '';

    const set = (campo, valor) => {
        setEstilos(prev => ({
            ...prev,
            pedidos_web: { ...ESTILOS_DEFAULT.pedidos_web, ...(prev.pedidos_web || {}), [campo]: valor }
        }));
        setCambiosPendientes(true);
    };

    // 🚩 Aplica los colores de una plantilla de un solo click, sin tocar
    // fuente ni bordes (esos van aparte, no son parte del "tema").
    const aplicarTema = (tema) => {
        setEstilos(prev => ({
            ...prev,
            pedidos_web: {
                ...ESTILOS_DEFAULT.pedidos_web,
                ...(prev.pedidos_web || {}),
                ...tema.colores,
            }
        }));
        setCambiosPendientes(true);
    };

    const temaEncontradoActivo = TEMAS_PREDEFINIDOS.find(t =>
        t.colores.color_primario === pw.color_primario && t.colores.color_fondo === pw.color_fondo
    );
    const temaActivoId = temaEncontradoActivo ? temaEncontradoActivo.id : null;

    const handleGuardar = async () => {
        setLoading(true);
        try {
            const res = await guardarEstilos(estilos);
            if (res && (res.status === 'ok' || res.status === 'success')) {
                toast.success('Cambios guardados');
                setCambiosPendientes(false);
            } else {
                toast.error('Error al guardar');
            }
        } catch (err) {
            toast.error('Error de conexión al guardar');
        } finally {
            setLoading(false);
        }
    };

    if (!tienePedidosWeb) {
        return (
            <div className="config-tienda">
                <div className="config-header">
                    <div className="config-header-left">
                        <div className="config-header-icon"><i className="bi bi-cart-check"></i></div>
                        <div>
                            <h1 className="config-header-title">Pedidos Web</h1>
                            <p className="config-header-subtitle">Personaliza el checkout de tu tienda online.</p>
                        </div>
                    </div>
                </div>
                <div className="config-content">
                    <div className="config-content-inner">
                        <div className="info-box">
                            <div className="info-box-icon"><i className="bi bi-lock-fill"></i></div>
                            <div>
                                <strong>Pedidos Web no está incluido en tu plan actual.</strong>
                                <div className="text-muted small mt-1">Disponible en los planes Menú QR y Restaurante.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (cargando) {
        return <div className="text-center py-5 text-muted">Cargando configuración...</div>;
    }

    return (
        <div className="config-tienda">
            <ToastContainer position="bottom-center" autoClose={2500} hideProgressBar />

            <div className="config-header">
                <div className="config-header-left">
                    <div className="config-header-icon"><i className="bi bi-cart-check"></i></div>
                    <div>
                        <h1 className="config-header-title">Pedidos Web</h1>
                        <p className="config-header-subtitle">Personaliza los colores y el estilo del checkout de tu tienda online.</p>
                    </div>
                </div>
            </div>

            <div className="config-body config-body-flat">
                <div className="config-content">
                    <div className="config-content-inner">
                        <div className="row g-4">

                            {/* COLUMNA NAV: SECCIONES */}
                            <div className="col-lg-2">
                                <nav className="config-seccion-nav" style={{ position: 'sticky', top: '90px' }}>
                                    {SECCIONES.map(s => (
                                        <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => setSeccionActiva(s.id)}
                                            className={`config-seccion-nav-link ${seccionActiva === s.id ? 'activa' : ''}`}
                                        >
                                            <i className={s.icono}></i> <span>{s.label}</span>
                                        </button>
                                    ))}
                                </nav>
                            </div>

                            {/* COLUMNA CENTRO: FORMULARIOS -- solo se muestra la sección activa */}
                            <div className="col-lg-6">
                                <ShopifyCard id="seccion-estado" activa={seccionActiva === 'seccion-estado'} icono="bi bi-toggle-on" titulo="Estado de la tienda">
                                    <p className="text-muted small mb-3">
                                        Botón de emergencia: si algo se complica en cocina o se te acaban los insumos, ciérrala acá
                                        y no se podrán hacer pedidos aunque estés dentro del horario configurado abajo.
                                    </p>
                                    <div className="d-flex" style={{ gap: '10px' }}>
                                        <button
                                            type="button"
                                            onClick={() => toggleAbierto(true)}
                                            className="btn btn-lg flex-fill fw-bold"
                                            style={abierto
                                                ? { background: '#22c55e', color: '#fff', border: 'none' }
                                                : { background: '#f1f3f5', color: '#495057', border: '1px solid #e2e8f0' }}
                                        >
                                            🟢 Abierta
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => toggleAbierto(false)}
                                            className="btn btn-lg flex-fill fw-bold"
                                            style={!abierto
                                                ? { background: '#dc3545', color: '#fff', border: 'none' }
                                                : { background: '#f1f3f5', color: '#495057', border: '1px solid #e2e8f0' }}
                                        >
                                            🔴 Cerrada ahora
                                        </button>
                                    </div>
                                    <div className="d-flex justify-content-end mt-3">
                                        {cambiosDispPendientes && (
                                            <span className="config-unsaved-badge mr-2 align-self-center">
                                                <i className="bi bi-exclamation-circle"></i> Sin guardar
                                            </span>
                                        )}
                                        <button type="button" className="config-btn-save" onClick={handleGuardarDisponibilidad} disabled={guardandoDisp}>
                                            {guardandoDisp
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className="bi bi-check-lg"></i>}
                                            Guardar estado
                                        </button>
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard id="seccion-horario" activa={seccionActiva === 'seccion-horario'} icono="bi bi-clock" titulo="Horario para tomar pedidos">
                                    <p className="text-muted small mb-3">
                                        Marca los días y horas en que aceptas pedidos online. Si dejas un día sin marcar,
                                        no se aceptan pedidos ese día aunque la tienda esté "Abierta". Si no configuras
                                        nada, la tienda acepta pedidos siempre mientras esté "Abierta".
                                    </p>
                                    {DIAS_SEMANA.map(d => (
                                        <div key={d.key} className="d-flex align-items-center mb-2" style={{ gap: '10px' }}>
                                            <div className="form-check" style={{ width: '110px', flexShrink: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id={`dia-${d.key}`}
                                                    checked={horario[d.key].activo}
                                                    onChange={(e) => setDiaHorario(d.key, 'activo', e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor={`dia-${d.key}`}>{d.nombre}</label>
                                            </div>
                                            <input
                                                type="time"
                                                className="config-input"
                                                style={{ maxWidth: '110px' }}
                                                value={horario[d.key].desde}
                                                disabled={!horario[d.key].activo}
                                                onChange={(e) => setDiaHorario(d.key, 'desde', e.target.value)}
                                            />
                                            <span className="text-muted">a</span>
                                            <input
                                                type="time"
                                                className="config-input"
                                                style={{ maxWidth: '110px' }}
                                                value={horario[d.key].hasta}
                                                disabled={!horario[d.key].activo}
                                                onChange={(e) => setDiaHorario(d.key, 'hasta', e.target.value)}
                                            />
                                        </div>
                                    ))}
                                    <div className="d-flex justify-content-end mt-3">
                                        {cambiosDispPendientes && (
                                            <span className="config-unsaved-badge mr-2 align-self-center">
                                                <i className="bi bi-exclamation-circle"></i> Sin guardar
                                            </span>
                                        )}
                                        <button type="button" className="config-btn-save" onClick={handleGuardarDisponibilidad} disabled={guardandoDisp}>
                                            {guardandoDisp
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className="bi bi-check-lg"></i>}
                                            Guardar horario y estado
                                        </button>
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard id="seccion-pagos" activa={seccionActiva === 'seccion-pagos'} icono="bi bi-credit-card" titulo="Métodos de pago">
                                    <p className="text-muted small mb-3">Elige qué formas de pago aceptas para cada tipo de entrega.</p>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="config-label">🛵 Delivery</label>
                                            {METODOS_PAGO_DISPONIBLES.map(m => (
                                                <div className="form-check mb-1" key={`delivery-${m.id}`}>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`delivery-${m.id}`}
                                                        checked={(metodosPago.delivery || []).includes(m.id)}
                                                        onChange={() => toggleMetodoPago('delivery', m.id)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`delivery-${m.id}`}>{m.label}</label>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="config-label">🏪 Retiro en local</label>
                                            {METODOS_PAGO_DISPONIBLES.map(m => (
                                                <div className="form-check mb-1" key={`retiro-${m.id}`}>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`retiro-${m.id}`}
                                                        checked={(metodosPago.retiro || []).includes(m.id)}
                                                        onChange={() => toggleMetodoPago('retiro', m.id)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`retiro-${m.id}`}>{m.label}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {transferenciaHabilitada && (
                                        <div className="mt-2 pt-3" style={{ borderTop: '1px solid #e2e8f0' }}>
                                            <label className="config-label mb-2">Datos bancarios para transferencia</label>
                                            <div className="config-grid-2">
                                                <div>
                                                    <label className="small text-muted d-block mb-1">Banco</label>
                                                    <input type="text" className="config-input" value={datosTransferencia.banco} onChange={(e) => setCampoTransferencia('banco', e.target.value)} placeholder="Banco Estado" />
                                                </div>
                                                <div>
                                                    <label className="small text-muted d-block mb-1">Tipo de cuenta</label>
                                                    <input type="text" className="config-input" value={datosTransferencia.tipo_cuenta} onChange={(e) => setCampoTransferencia('tipo_cuenta', e.target.value)} placeholder="Cuenta Vista" />
                                                </div>
                                                <div>
                                                    <label className="small text-muted d-block mb-1">N° de cuenta</label>
                                                    <input type="text" className="config-input" value={datosTransferencia.numero_cuenta} onChange={(e) => setCampoTransferencia('numero_cuenta', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="small text-muted d-block mb-1">Titular</label>
                                                    <input type="text" className="config-input" value={datosTransferencia.titular} onChange={(e) => setCampoTransferencia('titular', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="small text-muted d-block mb-1">RUT</label>
                                                    <input type="text" className="config-input" value={datosTransferencia.rut} onChange={(e) => setCampoTransferencia('rut', e.target.value)} />
                                                </div>
                                                <div>
                                                    <label className="small text-muted d-block mb-1">Email (comprobante)</label>
                                                    <input type="email" className="config-input" value={datosTransferencia.email} onChange={(e) => setCampoTransferencia('email', e.target.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-end mt-3">
                                        {cambiosDispPendientes && (
                                            <span className="config-unsaved-badge mr-2 align-self-center">
                                                <i className="bi bi-exclamation-circle"></i> Sin guardar
                                            </span>
                                        )}
                                        <button type="button" className="config-btn-save" onClick={handleGuardarDisponibilidad} disabled={guardandoDisp}>
                                            {guardandoDisp
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className="bi bi-check-lg"></i>}
                                            Guardar métodos de pago
                                        </button>
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard id="seccion-despacho" activa={seccionActiva === 'seccion-despacho'} icono="bi bi-geo-alt-fill" titulo="Zonas de despacho">
                                    <p className="text-muted small mb-3">¿Cómo cobras el envío?</p>
                                    <div className="btn-group w-100 mb-3" role="group">
                                        <button type="button" className={`btn ${modoDespacho === 'gratis' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => cambiarModoDespacho('gratis')}>
                                            🎁 Gratis
                                        </button>
                                        <button type="button" className={`btn ${modoDespacho === 'fijo' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => cambiarModoDespacho('fijo')}>
                                            💲 Monto fijo
                                        </button>
                                        <button type="button" className={`btn ${modoDespacho === 'zonas' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => cambiarModoDespacho('zonas')}>
                                            🗺️ Por sectores
                                        </button>
                                    </div>

                                    {modoDespacho === 'gratis' && (
                                        <p className="text-muted small mb-0">El envío sale $0 para cualquier dirección. No necesitas configurar zonas.</p>
                                    )}

                                    {modoDespacho === 'fijo' && (
                                        <div className="mb-2">
                                            <label className="config-label">Costo de envío</label>
                                            <div className="input-group" style={{ maxWidth: '220px' }}>
                                                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                                                <input type="number" className="config-input" value={costoDespachoFijo} onChange={(e) => { setCostoDespachoFijo(Number(e.target.value) || 0); setCambiosDispPendientes(true); }} />
                                            </div>
                                            <small className="text-muted d-block mt-1">Se cobra igual para cualquier dirección, no necesitas configurar zonas.</small>
                                        </div>
                                    )}

                                    {modoDespacho === 'zonas' && (
                                        <>
                                            <label className="config-label">Enfoque de las zonas</label>
                                            <div className="btn-group w-100 mb-2" role="group">
                                                <button type="button" className={`btn btn-sm ${tipoZonas === 'geocerca' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => cambiarTipoZonas('geocerca')}>
                                                    <i className="fas fa-draw-polygon mr-1"></i> Geocerca (mapa)
                                                </button>
                                                <button type="button" className={`btn btn-sm ${tipoZonas === 'nombre' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => cambiarTipoZonas('nombre')}>
                                                    <i className="fas fa-list mr-1"></i> Por nombre (lista)
                                                </button>
                                            </div>
                                            <p className="text-muted small mb-3">
                                                {tipoZonas === 'geocerca'
                                                    ? 'Dibujas el área de cada zona en el mapa; se detecta sola según la dirección del cliente.'
                                                    : 'Cada zona es solo un nombre y un costo; el cliente la elige de una lista, sin mapa.'}
                                                {' '}Aplica a todas las zonas de este negocio.
                                            </p>
                                            {cargandoSectores ? (
                                                <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"></div></div>
                                            ) : (
                                                <>
                                                    {sectores.length === 0 ? (
                                                        <p className="text-muted small mb-3">Todavía no tienes zonas configuradas.</p>
                                                    ) : (
                                                        <div className="mb-3">
                                                            {sectores.map(s => (
                                                                <div key={s.id} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f1f3f5' }}>
                                                                    <div>
                                                                        <span className="fw-bold">{s.nombre_sector}</span>{' '}
                                                                        <span className={`badge badge-pill ${s.poligono && s.poligono.length > 0 ? 'badge-soft-success' : 'badge-soft-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                                                            {s.poligono && s.poligono.length > 0 ? 'Geocerca' : 'Fija'}
                                                                        </span>
                                                                        <div className="text-muted small">{formatPrecioCLP(s.valor_entrega)} envío · mínimo {formatPrecioCLP(s.monto_minimo_pedido)}</div>
                                                                    </div>
                                                                    <div>
                                                                        <button className="btn btn-icon btn-light rounded-circle mr-1 text-primary" onClick={() => { setSectorAEditar(s); setModalSectorAbierto(true); }} title="Editar">
                                                                            <i className="fas fa-pen"></i>
                                                                        </button>
                                                                        <button className="btn btn-icon btn-light rounded-circle text-danger" onClick={() => handleEliminarSector(s.id, s.nombre_sector)} title="Eliminar">
                                                                            <i className="fas fa-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => { setSectorAEditar(null); setModalSectorAbierto(true); }}>
                                                        <i className="fas fa-plus mr-1"></i> Nueva zona
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    )}

                                    <div className="d-flex justify-content-end mt-3">
                                        {cambiosDispPendientes && (
                                            <span className="config-unsaved-badge mr-2 align-self-center">
                                                <i className="bi bi-exclamation-circle"></i> Sin guardar
                                            </span>
                                        )}
                                        <button type="button" className="config-btn-save" onClick={handleGuardarDisponibilidad} disabled={guardandoDisp}>
                                            {guardandoDisp
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className="bi bi-check-lg"></i>}
                                            Guardar modo de despacho
                                        </button>
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard id="seccion-diseno" activa={seccionActiva === 'seccion-diseno'} icono="bi bi-palette2" titulo="Diseño">
                                    <p className="text-muted small mb-3">Elige el estilo de tu tienda. Aplica colores e identidad visual de un click.</p>
                                    <div className="row row-cols-2 row-cols-md-3 g-3 mb-4">
                                        {TEMAS_PREDEFINIDOS.map(tema => (
                                            <div className="col" key={tema.id}>
                                                <TemaCard tema={tema} activo={temaActivoId === tema.id} onClick={() => aplicarTema(tema)} />
                                            </div>
                                        ))}
                                    </div>

                                    <label className="config-label mb-2" style={{ display: 'block' }}>Tipografía y forma</label>
                                    <div className="config-grid-2">
                                        <div>
                                            <label className="config-label">Fuente</label>
                                            <select className="config-input" value={pw.fuente} onChange={(e) => set('fuente', e.target.value)}>
                                                {FUENTES_DISPONIBLES.map(f => (
                                                    <option key={f.valor} value={f.valor}>{f.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="config-label">Bordes</label>
                                            <select className="config-input" value={pw.border_radius_general} onChange={(e) => set('border_radius_general', e.target.value)}>
                                                {RADIOS_DISPONIBLES.map(r => (
                                                    <option key={r.valor} value={r.valor}>{r.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end mt-3">
                                        {cambiosPendientes && (
                                            <span className="config-unsaved-badge mr-2 align-self-center">
                                                <i className="bi bi-exclamation-circle"></i> Sin guardar
                                            </span>
                                        )}
                                        <button type="button" className="config-btn-save" onClick={handleGuardar} disabled={loading}>
                                            {loading
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className="bi bi-check-lg"></i>}
                                            Guardar diseño
                                        </button>
                                    </div>
                                </ShopifyCard>

                                {/* Vista del catalogo: cuadricula (historico) o portada de
                                    categorias. Vive en el bloque pedidos_web; la carta QR tiene la suya. */}
                                <ShopifyCard id="seccion-vista-catalogo" activa={seccionActiva === 'seccion-vista-catalogo'} icono="bi bi-grid-3x3-gap" titulo="Vista del catálogo">
                                    <p className="text-muted small mb-3">Cómo ve el cliente tu carta al entrar a la tienda.</p>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        {[
                                            { val: 'grilla', label: 'Cuadricula', desc: 'Todos los productos de una' },
                                            { val: 'categorias', label: 'Lista de categorias', desc: 'Elige categoria y entra' },
                                        ].map(opt => {
                                            const activo = (pw.vista_catalogo || 'grilla') === opt.val;
                                            return (
                                                <button key={opt.val} onClick={() => set('vista_catalogo', opt.val)}
                                                    style={{
                                                        flex: 1, padding: '12px', borderRadius: '10px', textAlign: 'left', cursor: 'pointer',
                                                        border: `2px solid ${activo ? '#008060' : '#e2e8f0'}`,
                                                        background: activo ? '#f0fdf4' : '#fff'
                                                    }}>
                                                    <div style={{ marginBottom: '10px' }}>
                                                        {opt.val === 'grilla' ? (
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', width: '46px' }}>
                                                                {[0, 1, 2, 3].map(i => <div key={i} style={{ height: '16px', borderRadius: '3px', background: activo ? '#008060' : '#e2e8f0' }} />)}
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '46px' }}>
                                                                {[0, 1, 2].map(i => <div key={i} style={{ height: '9px', borderRadius: '3px', background: activo ? '#008060' : '#e2e8f0' }} />)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ fontWeight: 600, fontSize: '13px', color: activo ? '#008060' : '#374151' }}>{opt.label}</div>
                                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{opt.desc}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="d-flex justify-content-end mt-3">
                                        {cambiosPendientes && (
                                            <span className="config-unsaved-badge mr-2 align-self-center">
                                                <i className="bi bi-exclamation-circle"></i> Sin guardar
                                            </span>
                                        )}
                                        <button type="button" className="config-btn-save" onClick={handleGuardar} disabled={loading}>
                                            {loading
                                                ? <span className="spinner-border spinner-border-sm" />
                                                : <i className="bi bi-check-lg"></i>}
                                            Guardar vista del catálogo
                                        </button>
                                    </div>
                                </ShopifyCard>

                                <ShopifyCard id="seccion-qr" activa={seccionActiva === 'seccion-qr'} icono="bi bi-qr-code" titulo="Código QR de tu tienda">
                                    {urlSlug ? (
                                        <>
                                            <p className="text-muted small mb-2">Este es el link de tu tienda online (delivery/retiro):</p>
                                            <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: '16px' }}>
                                                <img
                                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(urlTienda)}`}
                                                    alt="Código QR de la tienda"
                                                    style={{ borderRadius: '10px', border: '1px solid #e2e8f0' }}
                                                />
                                                <div>
                                                    <a href={urlTienda} target="_blank" rel="noreferrer" className="fw-bold d-block mb-2" style={{ wordBreak: 'break-all' }}>
                                                        {urlTienda.replace(/^https?:\/\//, '')}
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="btn btn-dark btn-sm"
                                                        onClick={() => {
                                                            const a = document.createElement('a');
                                                            a.href = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(urlTienda)}`;
                                                            a.download = `qr-tienda-${urlSlug}.png`;
                                                            a.click();
                                                        }}
                                                    >
                                                        <i className="bi bi-download mr-1"></i> Descargar QR
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-muted small mb-0">
                                            Todavía no tienes una URL configurada. Ve a <a href="/configuracion/tienda/">Carta QR</a> y define la
                                            "URL de tu carta" primero — la tienda usa el mismo slug, sin ningún prefijo.
                                        </p>
                                    )}
                                </ShopifyCard>

                            </div>

                            {/* COLUMNA DERECHA: VISTA PREVIA */}
                            <div className="col-lg-4">
                                <div className="sticky-card">
                                    <div className="sticky-card-header">
                                        <span className="config-label mb-0">Vista previa</span>
                                        <span className="config-badge">Checkout</span>
                                    </div>
                                    <div className="sticky-card-body" style={{
                                        background: pw.color_fondo,
                                        color: pw.color_texto,
                                        fontFamily: pw.fuente,
                                        borderRadius: pw.border_radius_general
                                    }}>
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                                            <span style={{
                                                background: pw.categoria_activa_bg, color: pw.categoria_activa_texto,
                                                padding: '6px 14px', borderRadius: pw.border_radius_general, fontSize: 13, fontWeight: 600
                                            }}>Hamburguesas</span>
                                            <span style={{
                                                background: pw.categoria_inactiva_bg, color: pw.categoria_inactiva_texto,
                                                padding: '6px 14px', borderRadius: pw.border_radius_general, fontSize: 13, fontWeight: 600
                                            }}>Bebidas</span>
                                            <span style={{
                                                background: pw.categoria_inactiva_bg, color: pw.categoria_inactiva_texto,
                                                padding: '6px 14px', borderRadius: pw.border_radius_general, fontSize: 13, fontWeight: 600
                                            }}>Postres</span>
                                        </div>

                                        <div style={{
                                            background: '#fff', borderRadius: pw.border_radius_general,
                                            padding: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                        }}>
                                            <div style={{ fontWeight: 700, marginBottom: 4, color: pw.color_texto }}>Hamburguesa Clásica</div>
                                            <div style={{ fontSize: 12, color: pw.color_secundario, marginBottom: 8 }}>Con papas fritas incluidas</div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 700, color: pw.color_primario }}>$6.500</span>
                                                <button style={{
                                                    background: pw.color_btn_primario, color: pw.color_btn_texto,
                                                    border: 'none', padding: '6px 16px', borderRadius: pw.border_radius_general,
                                                    fontSize: 12, fontWeight: 700
                                                }}>Agregar</button>
                                            </div>
                                        </div>

                                        <button style={{
                                            width: '100%', background: pw.btn_continuar_bg, color: pw.btn_continuar_texto,
                                            border: 'none', padding: '12px', borderRadius: pw.border_radius_general,
                                            fontSize: 14, fontWeight: 700
                                        }}>Continuar al pago</button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .badge-soft-success { background-color: #e6fcf5; color: #087f5b; }
                .badge-soft-secondary { background-color: #f1f3f5; color: #495057; }
                .btn-icon { width: 32px; height: 32px; border: none; display: inline-flex; align-items: center; justify-content: center; transition: 0.2s; }
                .btn-icon:hover { background-color: #e9ecef; transform: scale(1.1); }
                .config-seccion-nav { display: flex; flex-direction: column; gap: 2px; }
                .config-seccion-nav-link {
                    display: flex; align-items: center; gap: 8px;
                    padding: 9px 12px; border-radius: 8px; border: none; background: transparent;
                    font-size: 13px; font-weight: 600; color: #495057; text-align: left;
                    text-decoration: none; transition: background 0.15s, color 0.15s; cursor: pointer; width: 100%;
                }
                .config-seccion-nav-link:hover { background: #f1f3f5; color: #1a1a2e; text-decoration: none; }
                .config-seccion-nav-link.activa { background: #008060; color: #fff; }
                .config-seccion-nav-link i { width: 16px; text-align: center; flex-shrink: 0; }
            `}</style>

            <SectorFormModal
                isVisible={modalSectorAbierto}
                onClose={() => { setModalSectorAbierto(false); setSectorAEditar(null); }}
                onSave={handleGuardarSector}
                sectorToEdit={sectorAEditar}
                tipoZonas={tipoZonas}
            />
        </div>
    );
}

export default ConfiguradorPedidosWeb;
